import type { JsonApiFunction } from '../cloudflare/types';
import * as api from './';

type Param<T extends string> = T extends `[[${string}]]` ? string[] : T extends `[${string}]` ? [string] : [];

type PathParams<S extends string> = S extends `${infer T}/${infer R}` ? [...Param<T>, ...PathParams<R>] : Param<S>;

interface JsonEndpoint<P extends string> {
    pathname: P;
}

interface JsonGetEndpoint<P extends string> extends JsonEndpoint<P> {
    onRequestGet: JsonApiFunction<P, any, any>;
}
interface JsonPostEndpoint<P extends string> extends JsonEndpoint<P> {
    onRequestPost: JsonApiFunction<P, any, any>;
}

type JsonApiResponse<T extends JsonApiFunction> = T extends JsonApiFunction<any, any, infer R> ? R : never;
type JsonApiRequest<T extends JsonApiFunction> = T extends JsonApiFunction<any, infer R, any> ? R : never;

type JsonClientWithParams<E extends JsonEndpoint<string>> = (E extends JsonGetEndpoint<infer P>
    ? {
          get(params: PathParams<P>, query?: api.QueryParams, init?: RequestInit): Promise<JsonApiResponse<E['onRequestGet']>>;
          getAnonymously(params: PathParams<P>, query?: api.QueryParams, init?: RequestInit): Promise<JsonApiResponse<E['onRequestGet']>>;
      }
    : Record<string, never>) &
    (E extends JsonPostEndpoint<infer P>
        ? {
              post(params: PathParams<P>, body: JsonApiRequest<E['onRequestPost']>): Promise<JsonApiResponse<E['onRequestPost']>>;
          }
        : Record<string, never>);

type JsonClientWithoutParams<E extends JsonEndpoint<string>> = (E extends JsonGetEndpoint<string>
    ? {
          get(query?: api.QueryParams, init?: RequestInit): Promise<JsonApiResponse<E['onRequestGet']>>;
          getAnonymously(query?: api.QueryParams, init?: RequestInit): Promise<JsonApiResponse<E['onRequestGet']>>;
      }
    : Record<string, never>) &
    (E extends JsonPostEndpoint<string>
        ? {
              post(body: JsonApiRequest<E['onRequestPost']>): Promise<JsonApiResponse<E['onRequestPost']>>;
          }
        : Record<string, never>);

type JsonClient<E extends JsonEndpoint<string>> = {
    cache<T>(key: string, query: (self: JsonClient<E>) => Promise<T>): Promise<T>;
} & (E['pathname'] extends `${string}[${string}]${string}` ? JsonClientWithParams<E> : JsonClientWithoutParams<E>);

export function createJsonClient<E extends JsonEndpoint<string>>(endpoint: E): JsonClient<E> {
    const withParams = endpoint.pathname.includes('[');

    const queries = new Map<string, Promise<any>>();

    const base: JsonClient<E> = {
        cache(key: string, query: (self: typeof base) => Promise<any>) {
            return queries.getOrInsertComputed(key, () => query(base));
        },
    } as any;

    if (withParams) {
        const result = base as JsonClientWithParams<E>;
        if ('onRequestGet' in endpoint) {
            result.get = (params: string[], query?: api.QueryParams, init?: RequestInit) =>
                api.get(createUrlFromParams(endpoint.pathname, params), query, init);
            result.getAnonymously = (params: string[], query?: api.QueryParams, init?: RequestInit) =>
                api.getAnonymously(createUrlFromParams(endpoint.pathname, params), query, init);
        }
        if ('onRequestPost' in endpoint) {
            result.post = (params: string[], body?: any) => api.postJson(createUrlFromParams(endpoint.pathname, params), body);
        }

        return result as JsonClient<E>;
    } else {
        const result = base as JsonClientWithoutParams<E>;
        if ('onRequestGet' in endpoint) {
            result.get = (query?: api.QueryParams, init?: RequestInit) => api.get(endpoint.pathname, query, init);
            result.getAnonymously = (query?: api.QueryParams, init?: RequestInit) => api.getAnonymously(endpoint.pathname, query, init);
        }
        if ('onRequestPost' in endpoint) {
            result.post = (body?: any) => api.postJson(endpoint.pathname, body);
        }

        return result as JsonClient<E>;
    }
}

function createUrlFromParams(pathname: string, params: string[]) {
    return pathname.replaceAll(/\[\w+\]/g, () => params.shift() ?? '');
}
